import { Injectable, signal, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of, forkJoin, switchMap, map, catchError } from 'rxjs';
import { environment } from '../../environments/environment';
import { handleError } from './handle-error';
import { SubscriptionResult } from '../model/subscription-billing';

// Re-export all types from model for backward compatibility
export {
  Feature,
  FeatureCreateRequest,
  FeatureCreateRequest as FeatureFormData, // Alias for backward compatibility
  FeatureUpdateRequest,
  FeatureType,
  FeatureCategory,
  ProductCategory,
  Product,
  ProductFormData,
  SubscriptionProduct,
  SubscriptionProductCreateRequest,
  SubscriptionProductCreateRequest as SubscriptionProductFormData, // Alias for backward compatibility
  SubscriptionProductUpdateRequest,
  MeteredProduct,
  MeteredProductCreateRequest,
  MeteredProductCreateRequest as MeteredProductFormData, // Alias for backward compatibility
  MeteredProductUpdateRequest,
  Plan,
  PlanItem,
  PlanCreateRequest,
  PlanCreateRequest as PlanFormData, // Alias for backward compatibility
  PlanUpdateRequest,
  Subscription,
  SubscriptionStatus,
  SubscriptionCreateRequest,
  SubscriptionFromPlanRequest,
  SubscriptionUpdateRequest,
  SubscriptionItem,
  SubscriptionItemCreateRequest,
  SubscriptionItemUpdateRequest,
  SubscriptionItemPayload,
  BillingInterval
} from '../model/product-management.model';

import {
  Feature,
  FeatureCreateRequest,
  FeatureUpdateRequest,
  FeatureCategory,
  FeatureType,
  ProductCategory,
  Product,
  ProductFormData,
  SubscriptionProduct,
  SubscriptionProductCreateRequest,
  SubscriptionProductUpdateRequest,
  MeteredProduct,
  MeteredProductCreateRequest,
  MeteredProductUpdateRequest,
  Plan,
  PlanItem,
  PlanCreateRequest,
  PlanUpdateRequest,
  Subscription,
  SubscriptionStatus,
  SubscriptionCreateRequest,
  SubscriptionFromPlanRequest,
  SubscriptionUpdateRequest,
  SubscriptionItem,
  SubscriptionItemCreateRequest,
  SubscriptionItemUpdateRequest,
  BillingInterval
} from '../model/product-management.model';

// =============================================================================
// Backend response shapes (odin / moneta)
// =============================================================================

interface PaginatedResult<T> {
  items: T[];
  total?: number;
  nextKey?: string;
}

interface FeatureResponse {
  id: string;
  code: string;
  name: string;
  description?: string;
  type: string;
  category: string;
  unitName?: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

interface SubscriptionProductResponse {
  id: string;
  featureId: string;
  featureCode: string;
  featureName: string;
  featureType: string;
  featureCategory: string;
  billingInterval: string;
  price: number;
  currency: string;
  taxCode?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

interface MeteredProductResponse {
  id: string;
  featureId: string;
  featureCode: string;
  featureName: string;
  name: string;
  metricKey: string;
  unitPrice: number;
  unitQuantity: number;
  currency: string;
  description?: string;
  taxCode?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

interface PlanItemResponse {
  id: string;
  subscriptionProductId: string;
  featureCode: string;
  featureName: string;
  /** the product's current list price */
  price: number;
  /** the plan's price for this item (FT-228); absent on an odin before it */
  unitPrice?: number;
  billingInterval: string;
  sortOrder: number;
  createdAt: string;
}

interface PlanResponse {
  id: string;
  name: string;
  description?: string;
  trialDays: number;
  isActive: boolean;
  sortOrder: number;
  items: PlanItemResponse[];
  /** sum of the items' unitPrice */
  totalPrice: number;
  /** sum of the items' list price (FT-228) */
  listTotalPrice?: number;
  currency?: string;
  billingInterval?: string;
  createdAt: string;
}

interface SubscriptionItemResponse {
  id: string;
  subscriptionProductId: string;
  featureName: string;
  featureCode: string;
  unitPrice: number;
  quantity: number;
  locationId?: string;
  locationName?: string;
  createdAt: string;
}

interface SubscriptionResponse {
  id: string;
  businessId: string;
  planId?: string;
  billingInterval: string;
  status: string;
  currency: string;
  startDate?: string;            // absent while parked: bought at intake, billing starts at go-live
  trialEndDate?: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  canceledAt?: string;
  lastBilledAt?: string;
  items: SubscriptionItemResponse[];
  createdAt: string;
}

interface LocationItemsGroup {
  locationId?: string;
  locationName: string;
  items: SubscriptionItemResponse[];
}

interface BusinessSubscriptionsResponse {
  businessId: string;
  businessName: string;
  subscription?: SubscriptionResponse;
  itemsByLocation: LocationItemsGroup[];
}

@Injectable({
  providedIn: 'root'
})
export class ProductManagementService {
  private http = inject(HttpClient);

  // Odin gateway path. Mercury services use a similar pattern (/mercury/admin/v1).
  private readonly monetaV1 = `${environment.baseUrl}/moneta/v1`;

  private readonly featuresUrl = `${this.monetaV1}/features`;
  private readonly subscriptionProductsUrl = `${this.monetaV1}/subscription-products`;
  private readonly meteredProductsUrl = `${this.monetaV1}/metered-products`;
  private readonly plansUrl = `${this.monetaV1}/plans`;

  // Subscriptions are nested under business: /v1/businesses/{businessId}/subscriptions
  private subscriptionsUrl(businessId: string): string {
    return `${this.monetaV1}/businesses/${businessId}/subscriptions`;
  }

  // odin requires an Idempotency-Key on subscription writes. Fresh key per call.
  private idempotencyHeaders(): HttpHeaders {
    const key = typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    return new HttpHeaders({ 'Idempotency-Key': key });
  }

  // Reactive counts surfaced on the Product Management tabs. Child list
  // components push their current list length here on every load/mutation;
  // the parent reads them for tab badges.
  readonly featuresCount = signal(0);
  readonly subscriptionProductsCount = signal(0);
  readonly meteredProductsCount = signal(0);
  readonly plansCount = signal(0);

  // ---------------------------------------------------------------------------
  // FEATURES — GET/POST/PATCH /v1/features
  // ---------------------------------------------------------------------------

  /**
   * RETHROWS. An empty catalog and a failed read look identical once a fallback
   * recovers, and the Features tab offers "load the catalog seed" on the empty
   * one — seeding against a catalog we could not read duplicates it.
   */
  getFeatures(): Observable<Feature[]> {
    const params = new HttpParams().set('limit', '200');
    return this.http.get<PaginatedResult<FeatureResponse>>(this.featuresUrl, { params }).pipe(
      map(res => (res?.items ?? []).map(this.mapFeature)),
      catchError(handleError('getFeatures'))
    );
  }

  getFeatureById(id: string): Observable<Feature | null> {
    return this.http.get<FeatureResponse>(`${this.featuresUrl}/${id}`).pipe(
      map(res => res ? this.mapFeature(res) : null),
      catchError(handleError<Feature | null>('getFeatureById', null))
    );
  }

  createFeature(data: FeatureCreateRequest): Observable<Feature> {
    // Backend `CreateFeatureRequest` does not accept `isActive` (defaults to true).
    const body = {
      code: data.code,
      name: data.name,
      description: data.description,
      type: data.type,
      category: data.category,
      unitName: data.unitName,
      sortOrder: data.sortOrder
    };
    return this.http.post<FeatureResponse>(this.featuresUrl, body).pipe(
      map(this.mapFeature),
      catchError(handleError<Feature>('createFeature'))
    );
  }

  updateFeature(id: string, data: FeatureUpdateRequest): Observable<Feature | null> {
    // Backend `UpdateFeatureRequest` only accepts these four. `code`, `type`,
    // `category` and `unitName` are immutable — the form still sends them
    // (getRawValue includes disabled controls), so drop them here.
    const body = {
      name: data.name,
      description: data.description,
      sortOrder: data.sortOrder,
      isActive: data.isActive
    };
    return this.http.patch<FeatureResponse>(`${this.featuresUrl}/${id}`, body).pipe(
      map(res => res ? this.mapFeature(res) : null),
      catchError(handleError<Feature | null>('updateFeature', null))
    );
  }

  /**
   * Soft-delete: backend has no DELETE endpoint for features.
   * Calling this PATCHes `{isActive:false}`. The row remains in the database.
   */
  deleteFeature(id: string): Observable<boolean> {
    return this.http.patch<FeatureResponse>(`${this.featuresUrl}/${id}`, { isActive: false }).pipe(
      map(() => true),
      catchError(handleError<boolean>('deleteFeature', false))
    );
  }

  getAvailableFeaturesForSubscription(): Observable<Feature[]> {
    const params = new HttpParams()
      .set('limit', '200')
      .set('type', 'BOOLEAN')
      .set('isActive', 'true');
    return this.http.get<PaginatedResult<FeatureResponse>>(this.featuresUrl, { params }).pipe(
      map(res => (res?.items ?? []).map(this.mapFeature)),
      catchError(handleError<Feature[]>('getAvailableFeaturesForSubscription', []))
    );
  }

  getAvailableFeaturesForMetered(): Observable<Feature[]> {
    const params = new HttpParams()
      .set('limit', '200')
      .set('type', 'METERED')
      .set('isActive', 'true');
    return this.http.get<PaginatedResult<FeatureResponse>>(this.featuresUrl, { params }).pipe(
      map(res => (res?.items ?? []).map(this.mapFeature)),
      catchError(handleError<Feature[]>('getAvailableFeaturesForMetered', []))
    );
  }

  // ---------------------------------------------------------------------------
  // PRODUCT CATEGORIES — backend gap (no HTTP API exposed)
  // ---------------------------------------------------------------------------

  // TODO: backend gap — odin does not yet expose Product Category CRUD over HTTP.
  // Returns empty list until the backend ships endpoints under `/v1/product-categories`.
  getProductCategories(): Observable<ProductCategory[]> {
    return of([]);
  }

  getCategories(): Observable<ProductCategory[]> {
    return this.getProductCategories();
  }

  // ---------------------------------------------------------------------------
  // PHYSICAL PRODUCTS — backend gap (no HTTP API exposed)
  // ---------------------------------------------------------------------------

  // TODO: backend gap — odin has Product domain models but no HTTP API yet.
  // All physical-product methods return empty/null until `/v1/products` ships.
  getProducts(): Observable<Product[]> {
    return of([]);
  }

  getProductById(_id: string): Observable<Product | null> {
    return of(null);
  }

  createProduct(_data: ProductFormData): Observable<Product> {
    // No backend endpoint. Caller will see this throw via handleError default.
    return of(null as unknown as Product);
  }

  updateProduct(_id: string, _data: Partial<ProductFormData>): Observable<Product | null> {
    return of(null);
  }

  deleteProduct(_id: string): Observable<boolean> {
    return of(false);
  }

  // ---------------------------------------------------------------------------
  // SUBSCRIPTION PRODUCTS — GET/POST/PATCH /v1/subscription-products
  // ---------------------------------------------------------------------------

  /** RETHROWS, for the reason on `getFeatures`. */
  getSubscriptionProducts(): Observable<SubscriptionProduct[]> {
    const params = new HttpParams().set('limit', '200');
    return this.http.get<PaginatedResult<SubscriptionProductResponse>>(this.subscriptionProductsUrl, { params }).pipe(
      map(res => (res?.items ?? []).map(this.mapSubscriptionProduct)),
      catchError(handleError('getSubscriptionProducts'))
    );
  }

  getSubscriptionProductById(id: string): Observable<SubscriptionProduct | null> {
    return this.http.get<SubscriptionProductResponse>(`${this.subscriptionProductsUrl}/${id}`).pipe(
      map(res => res ? this.mapSubscriptionProduct(res) : null),
      catchError(handleError<SubscriptionProduct | null>('getSubscriptionProductById', null))
    );
  }

  createSubscriptionProduct(data: SubscriptionProductCreateRequest): Observable<SubscriptionProduct> {
    // Backend create body does not accept `trialDays` (lives on Plan) or `isActive` (defaults true).
    const body = {
      featureId: data.featureId,
      billingInterval: data.billingInterval,
      price: data.price,
      currency: data.currency,
      taxCode: data.taxCode
    };
    return this.http.post<SubscriptionProductResponse>(this.subscriptionProductsUrl, body).pipe(
      map(this.mapSubscriptionProduct),
      catchError(handleError<SubscriptionProduct>('createSubscriptionProduct'))
    );
  }

  updateSubscriptionProduct(id: string, data: SubscriptionProductUpdateRequest): Observable<SubscriptionProduct | null> {
    // Backend update does not accept `trialDays` (lives on Plan).
    const body = {
      price: data.price,
      taxCode: data.taxCode,
      isActive: data.isActive
    };
    return this.http.patch<SubscriptionProductResponse>(`${this.subscriptionProductsUrl}/${id}`, body).pipe(
      map(res => res ? this.mapSubscriptionProduct(res) : null),
      catchError(handleError<SubscriptionProduct | null>('updateSubscriptionProduct', null))
    );
  }

  /**
   * Soft-delete: backend has no DELETE endpoint. PATCHes `isActive=false`.
   */
  deleteSubscriptionProduct(id: string): Observable<boolean> {
    return this.http.patch<SubscriptionProductResponse>(`${this.subscriptionProductsUrl}/${id}`, { isActive: false }).pipe(
      map(() => true),
      catchError(handleError<boolean>('deleteSubscriptionProduct', false))
    );
  }

  /** RETHROWS: the plan form must not offer an empty picker that means "read failed". */
  getSubscriptionProductsForInterval(interval: BillingInterval): Observable<SubscriptionProduct[]> {
    const params = new HttpParams()
      .set('limit', '200')
      .set('billingInterval', interval)
      .set('isActive', 'true');
    return this.http.get<PaginatedResult<SubscriptionProductResponse>>(this.subscriptionProductsUrl, { params }).pipe(
      map(res => (res?.items ?? []).map(this.mapSubscriptionProduct)),
      catchError(handleError('getSubscriptionProductsForInterval'))
    );
  }

  // ---------------------------------------------------------------------------
  // METERED PRODUCTS — GET/POST/PATCH /v1/metered-products
  // ---------------------------------------------------------------------------

  getMeteredProducts(): Observable<MeteredProduct[]> {
    const params = new HttpParams().set('limit', '200');
    return this.http.get<PaginatedResult<MeteredProductResponse>>(this.meteredProductsUrl, { params }).pipe(
      map(res => (res?.items ?? []).map(this.mapMeteredProduct)),
      catchError(handleError('getMeteredProducts'))
    );
  }

  getMeteredProductById(id: string): Observable<MeteredProduct | null> {
    return this.http.get<MeteredProductResponse>(`${this.meteredProductsUrl}/${id}`).pipe(
      map(res => res ? this.mapMeteredProduct(res) : null),
      catchError(handleError<MeteredProduct | null>('getMeteredProductById', null))
    );
  }

  createMeteredProduct(data: MeteredProductCreateRequest): Observable<MeteredProduct> {
    // Backend create body does not accept `isActive` (defaults true).
    const body = {
      featureId: data.featureId,
      name: data.name,
      metricKey: data.metricKey,
      unitPrice: data.unitPrice,
      unitQuantity: data.unitQuantity,
      currency: data.currency,
      description: data.description,
      taxCode: data.taxCode
    };
    return this.http.post<MeteredProductResponse>(this.meteredProductsUrl, body).pipe(
      map(this.mapMeteredProduct),
      catchError(handleError<MeteredProduct>('createMeteredProduct'))
    );
  }

  updateMeteredProduct(id: string, data: MeteredProductUpdateRequest): Observable<MeteredProduct | null> {
    // Backend `UpdateMeteredProductRequest` only accepts these five. `featureId`,
    // `metricKey`, `unitQuantity` and `currency` are immutable — the form sends
    // the full create shape, so drop them here.
    const body = {
      name: data.name,
      unitPrice: data.unitPrice,
      description: data.description,
      taxCode: data.taxCode,
      isActive: data.isActive
    };
    return this.http.patch<MeteredProductResponse>(`${this.meteredProductsUrl}/${id}`, body).pipe(
      map(res => res ? this.mapMeteredProduct(res) : null),
      catchError(handleError<MeteredProduct | null>('updateMeteredProduct', null))
    );
  }

  /**
   * Soft-delete: backend has no DELETE endpoint. PATCHes `isActive=false`.
   */
  deleteMeteredProduct(id: string): Observable<boolean> {
    return this.http.patch<MeteredProductResponse>(`${this.meteredProductsUrl}/${id}`, { isActive: false }).pipe(
      map(() => true),
      catchError(handleError<boolean>('deleteMeteredProduct', false))
    );
  }

  // ---------------------------------------------------------------------------
  // PLANS — GET/POST/PATCH /v1/plans
  // ---------------------------------------------------------------------------

  getPlans(): Observable<Plan[]> {
    // Backend list endpoint is unpaginated; isActive defaults to true server-side.
    // Pass `isActive=false` to include inactive plans, but for now the UI shows active only.
    return this.http.get<PlanResponse[]>(this.plansUrl).pipe(
      map(res => (res ?? []).map(this.mapPlan)),
      catchError(handleError('getPlans'))
    );
  }

  getPlanById(id: string): Observable<Plan | null> {
    return this.http.get<PlanResponse>(`${this.plansUrl}/${id}`).pipe(
      map(res => res ? this.mapPlan(res) : null),
      catchError(handleError<Plan | null>('getPlanById', null))
    );
  }

  createPlan(data: PlanCreateRequest): Observable<Plan> {
    // Backend `CreatePlanRequest` derives billingInterval and currency from items;
    // does not accept `isActive` (defaults true). Items are passed embedded.
    const body = {
      name: data.name,
      description: data.description,
      trialDays: data.trialDays,
      sortOrder: data.sortOrder ?? 0,
      items: data.itemSubscriptionProductIds.map((spId, i) => ({
        subscriptionProductId: spId,
        sortOrder: i,
        unitPrice: data.itemPrices?.[spId]
      }))
    };
    return this.http.post<PlanResponse>(this.plansUrl, body).pipe(
      map(this.mapPlan),
      catchError(handleError<Plan>('createPlan'))
    );
  }

  /**
   * Update plan-level fields and (if `itemSubscriptionProductIds` provided)
   * diff items against current plan, firing add/remove calls in parallel.
   *
   * Note: backend update body does NOT accept trialDays — that field is silently
   * dropped. Item changes use POST /v1/plans/{id}/items and DELETE /v1/plans/{id}/items/{itemId}.
   */
  updatePlan(id: string, data: PlanUpdateRequest): Observable<Plan | null> {
    const planBody: Record<string, unknown> = {};
    if (data.name !== undefined) planBody['name'] = data.name;
    if (data.description !== undefined) planBody['description'] = data.description;
    if (data.isActive !== undefined) planBody['isActive'] = data.isActive;
    if (data.sortOrder !== undefined) planBody['sortOrder'] = data.sortOrder;

    const updatePlanFields$: Observable<unknown> = Object.keys(planBody).length > 0
      ? this.http.patch<PlanResponse>(`${this.plansUrl}/${id}`, planBody)
      : of(null);

    return updatePlanFields$.pipe(
      switchMap(() => {
        // No item changes requested — fetch fresh plan and return.
        if (!data.itemSubscriptionProductIds) {
          return this.getPlanById(id);
        }
        return this.applyPlanItemDiff(id, data.itemSubscriptionProductIds, data.itemPrices ?? {}).pipe(
          switchMap(() => this.getPlanById(id))
        );
      }),
      catchError(handleError<Plan | null>('updatePlan', null))
    );
  }

  /**
   * Soft-delete: backend has no DELETE endpoint. PATCHes `isActive=false`.
   */
  deletePlan(id: string): Observable<boolean> {
    return this.http.patch<PlanResponse>(`${this.plansUrl}/${id}`, { isActive: false }).pipe(
      map(() => true),
      catchError(handleError<boolean>('deletePlan', false))
    );
  }

  /**
   * Diff the desired item list against the current plan and fire add, remove and price calls in
   * parallel: a new item is added at its plan price, a kept item whose price moved is PATCHed.
   * Returns once all changes complete.
   */
  private applyPlanItemDiff(planId: string, desiredSpIds: string[], prices: Record<string, number>): Observable<unknown> {
    return this.http.get<PlanResponse>(`${this.plansUrl}/${planId}`).pipe(
      switchMap(plan => {
        const currentItems = plan?.items ?? [];
        const currentSpIds = new Set(currentItems.map(i => i.subscriptionProductId));
        const desiredSet = new Set(desiredSpIds);

        const toAdd = desiredSpIds.filter(spId => !currentSpIds.has(spId));
        const toRemove = currentItems.filter(i => !desiredSet.has(i.subscriptionProductId));

        const addCalls: Observable<unknown>[] = toAdd.map((spId, i) =>
          this.http.post<PlanResponse>(`${this.plansUrl}/${planId}/items`, {
            subscriptionProductId: spId,
            sortOrder: currentItems.length + i,
            unitPrice: prices[spId]
          })
        );
        const removeCalls: Observable<unknown>[] = toRemove.map(item =>
          this.http.delete<void>(`${this.plansUrl}/${planId}/items/${item.id}`)
        );
        const priceCalls: Observable<unknown>[] = currentItems
          .filter(i => desiredSet.has(i.subscriptionProductId))
          .filter(i => prices[i.subscriptionProductId] !== undefined && prices[i.subscriptionProductId] !== (i.unitPrice ?? i.price))
          .map(i => this.http.patch<PlanResponse>(`${this.plansUrl}/${planId}/items/${i.id}`, { unitPrice: prices[i.subscriptionProductId] }));

        const all: Observable<unknown>[] = [...addCalls, ...removeCalls, ...priceCalls];
        return all.length === 0 ? of(null) : forkJoin(all);
      })
    );
  }

  // ---------------------------------------------------------------------------
  // SUBSCRIPTIONS — GET/POST/PUT/DELETE /v1/businesses/{businessId}/subscriptions
  //
  // Phase 2 (wired). Subscriptions are scoped to a business; every method takes
  // a `businessId`. Most write operations return the full SubscriptionResponse
  // (so the caller can refresh local state without an extra GET).
  // ---------------------------------------------------------------------------

  /**
   * GET /v1/businesses/{businessId}/subscriptions
   * Returns the single Subscription for a business (extracted from
   * `BusinessSubscriptionsResponse.subscription`). Null if the business has
   * never subscribed.
   *
   * RETHROWS. "No subscription" and "could not ask" are different answers and
   * only the caller knows which one it can live with: onboarding must not offer
   * a second plan (moneta refuses it with a 409) and go-live must not report a
   * client as unsubscribed, so both need to see the failure. Callers that are
   * happy either way add their own `catchError`.
   */
  getSubscriptionByBusinessId(businessId: string): Observable<Subscription | null> {
    return this.http.get<BusinessSubscriptionsResponse>(this.subscriptionsUrl(businessId)).pipe(
      map(res => res?.subscription ? this.mapSubscription(res.subscription) : null),
      catchError(handleError('getSubscriptionByBusinessId'))
    );
  }

  /** GET /v1/businesses/{businessId}/subscriptions/{id} */
  getSubscriptionById(businessId: string, id: string): Observable<Subscription | null> {
    return this.http.get<SubscriptionResponse>(`${this.subscriptionsUrl(businessId)}/${id}`).pipe(
      map(res => res ? this.mapSubscription(res) : null),
      catchError(handleError<Subscription | null>('getSubscriptionById', null))
    );
  }

  /**
   * POST /v1/businesses/{businessId}/subscriptions
   * Backend `CreateSubscriptionRequest` embeds items in the body.
   * `data.businessId`, `data.startDate`, `data.termMonths`, `data.autoRenew`
   * are not on the wire format and are dropped.
   */
  createSubscription(businessId: string, data: SubscriptionCreateRequest): Observable<Subscription | null> {
    const body = {
      billingInterval: data.billingInterval,
      currency: data.currency,
      items: (data.items ?? []).map(it => ({
        subscriptionProductId: it.subscriptionProductId,
        quantity: it.quantity ?? 1,
        unitPriceOverride: it.unitPriceOverride,
        locationId: it.locationId
      }))
    };
    return this.http.post<SubscriptionResponse>(this.subscriptionsUrl(businessId), body, { headers: this.idempotencyHeaders() }).pipe(
      map(res => res ? this.mapSubscription(res) : null),
      catchError(handleError<Subscription | null>('createSubscription', null))
    );
  }

  /**
   * POST /v1/businesses/{businessId}/subscriptions:from-plan
   *
   * Colon action, not a sub-path: the segment is `subscriptions:from-plan`, so
   * there is no slash before the colon and the action is kebab-case
   * (`SubscriptionApi.kt:44`). The previous `/:fromPlan` form 404'd.
   */
  createSubscriptionFromPlan(businessId: string, data: SubscriptionFromPlanRequest): Observable<Subscription | null> {
    return this.http.post<SubscriptionResponse>(`${this.subscriptionsUrl(businessId)}:from-plan`, data, { headers: this.idempotencyHeaders() }).pipe(
      map(res => res ? this.mapSubscription(res) : null),
      catchError(handleError<Subscription | null>('createSubscriptionFromPlan', null))
    );
  }

  /**
   * Plan-mode create, both phases. `:from-plan` seeds one business-wide item
   * per plan product at the plan's price and accepts nothing else (BE-confirmed:
   * only planId/currency/locationId), so wizard customizations — price
   * override, location scoping — are applied after: each divergent item is
   * DELETEd and re-POSTed with `unitPriceOverride` (cents) / `locationId`.
   * The item PUT only accepts quantity, so replace is the only way.
   *
   * Every re-POST carries its price: the AM's when edited, else the plan's. An item
   * POST without an override bills at list price, which would undo the plan (FT-230).
   *
   * Timing is safe: the first invoice is built when the period closes, from
   * the item list at that moment, so phase-2 calls land in the first bill.
   *
   * If any DELETE fails the adds are skipped entirely — re-adding next to a
   * surviving seeded item would double-bill the feature next period.
   * `customizationsFailed` tells the caller to warn the user either way.
   */
  createSubscriptionFromPlanCustomized(
    businessId: string,
    result: SubscriptionResult,
  ): Observable<{ subscription: Subscription | null; customizationsFailed: boolean }> {
    return this.createSubscriptionFromPlan(businessId, { planId: result.planId ?? '' }).pipe(
      switchMap(sub => {
        if (!sub) return of({ subscription: null, customizationsFailed: false });

        const deletes: Observable<Subscription | null>[] = [];
        const adds: SubscriptionItemCreateRequest[] = [];
        for (const want of result.items) {
          const businessWide = want.locationIds === null;
          // Business-wide at plan price is exactly what :from-plan seeded — keep it.
          if (businessWide && !want.isCustomPrice) continue;
          sub.items
            .filter(i => i.subscriptionProductId === want.subscriptionProductId)
            .forEach(i => deletes.push(this.removeSubscriptionItem(businessId, sub.id, i.id)));
          // want.unitPrice is the AM's price when edited and the plan's otherwise (dollars)
          const unitPriceOverride = Math.round(want.unitPrice * 100);
          for (const locationId of businessWide ? [undefined] : want.locationIds!) {
            adds.push({
              subscriptionProductId: want.subscriptionProductId,
              quantity: want.quantity ?? 1,
              locationId,
              unitPriceOverride,
            });
          }
        }
        if (!deletes.length && !adds.length) {
          return of({ subscription: sub, customizationsFailed: false });
        }

        // forkJoin([]) completes without emitting — guard the empty case.
        const runDeletes = deletes.length ? forkJoin(deletes) : of([] as (Subscription | null)[]);
        return runDeletes.pipe(
          switchMap(deleted => {
            if (deleted.some(d => !d)) {
              return of({ subscription: sub, customizationsFailed: true });
            }
            if (!adds.length) return of({ subscription: sub, customizationsFailed: false });
            return forkJoin(adds.map(a => this.addSubscriptionItem(businessId, sub.id, a))).pipe(
              map(added => ({
                subscription: added.filter((x): x is Subscription => !!x).pop() ?? sub,
                customizationsFailed: added.some(x => !x),
              })),
            );
          }),
        );
      }),
    );
  }

  /**
   * PUT /v1/businesses/{businessId}/subscriptions/{id}
   * Used for billingInterval change and status transitions (pause/resume use
   * `pauseSubscription` / `resumeSubscription` helpers below).
   */
  updateSubscription(businessId: string, id: string, data: SubscriptionUpdateRequest): Observable<Subscription | null> {
    return this.http.put<SubscriptionResponse>(`${this.subscriptionsUrl(businessId)}/${id}`, data, { headers: this.idempotencyHeaders() }).pipe(
      map(res => res ? this.mapSubscription(res) : null),
      catchError(handleError<Subscription | null>('updateSubscription', null))
    );
  }

  /**
   * POST /v1/businesses/{businessId}/subscriptions/{subId}/items.
   * Body mirrors `AddSubscriptionItemRequest`: SP UUID + quantity + optional
   * `locationId` (UUID) + optional `unitPriceOverride` (cents). Returns the
   * full Subscription so the caller can diff items.
   */
  addSubscriptionItem(businessId: string, subscriptionId: string, data: SubscriptionItemCreateRequest): Observable<Subscription | null> {
    const body: Record<string, unknown> = {
      subscriptionProductId: data.subscriptionProductId,
      quantity: data.quantity ?? 1
    };
    if (data.locationId) body['locationId'] = data.locationId;
    if (data.unitPriceOverride != null) body['unitPriceOverride'] = data.unitPriceOverride;
    return this.http.post<SubscriptionResponse>(`${this.subscriptionsUrl(businessId)}/${subscriptionId}/items`, body, { headers: this.idempotencyHeaders() }).pipe(
      map(res => res ? this.mapSubscription(res) : null),
      catchError(handleError<Subscription | null>('addSubscriptionItem', null))
    );
  }

  /**
   * PUT /v1/businesses/{businessId}/subscriptions/{subId}/items/{itemId}
   * Backend `UpdateSubscriptionItemRequest` only accepts `quantity`. Frontend's
   * `discountPercent` / `discountEndDate` are not on the wire format.
   */
  updateSubscriptionItem(businessId: string, subscriptionId: string, itemId: string, data: SubscriptionItemUpdateRequest): Observable<Subscription | null> {
    const body = { quantity: data.quantity };
    return this.http.put<SubscriptionResponse>(`${this.subscriptionsUrl(businessId)}/${subscriptionId}/items/${itemId}`, body, { headers: this.idempotencyHeaders() }).pipe(
      map(res => res ? this.mapSubscription(res) : null),
      catchError(handleError<Subscription | null>('updateSubscriptionItem', null))
    );
  }

  /** DELETE /v1/businesses/{businessId}/subscriptions/{subId}/items/{itemId} — returns the full Subscription */
  removeSubscriptionItem(businessId: string, subscriptionId: string, itemId: string): Observable<Subscription | null> {
    return this.http.delete<SubscriptionResponse>(`${this.subscriptionsUrl(businessId)}/${subscriptionId}/items/${itemId}`, { headers: this.idempotencyHeaders() }).pipe(
      map(res => res ? this.mapSubscription(res) : null),
      catchError(handleError<Subscription | null>('removeSubscriptionItem', null))
    );
  }

  /**
   * DELETE /v1/businesses/{businessId}/subscriptions/{subId} — soft cancel.
   * Optional `endReason` is sent as a JSON body (CancelSubscriptionRequest).
   * Backend defaults to CANCELLED_BY_ADMIN if absent.
   */
  cancelSubscription(
    businessId: string,
    subscriptionId: string,
    endReason?: string
  ): Observable<Subscription | null> {
    const url = `${this.subscriptionsUrl(businessId)}/${subscriptionId}`;
    const options = { headers: this.idempotencyHeaders(), ...(endReason ? { body: { endReason } } : {}) };
    return this.http.delete<SubscriptionResponse>(url, options).pipe(
      map(res => res ? this.mapSubscription(res) : null),
      catchError(handleError<Subscription | null>('cancelSubscription', null))
    );
  }

  /** PUT subscription with status=PAUSED. Backend reuses UpdateSubscriptionRequest. */
  pauseSubscription(businessId: string, subscriptionId: string): Observable<Subscription | null> {
    return this.updateSubscription(businessId, subscriptionId, { status: 'PAUSED' });
  }

  /** PUT subscription with status=ACTIVE. */
  resumeSubscription(businessId: string, subscriptionId: string): Observable<Subscription | null> {
    return this.updateSubscription(businessId, subscriptionId, { status: 'ACTIVE' });
  }

  // TODO: backend gap — no atomic switch-interval endpoint. Dropped per Q7.
  // The PUT endpoint accepts billingInterval but mid-period switches have
  // pricing/proration implications the backend doesn't yet handle.
  switchBillingInterval(_businessId: string, _subscriptionId: string, _newInterval: BillingInterval): Observable<Subscription | null> {
    return of(null);
  }

  // TODO: backend gap — no renew endpoint. Dropped per Q8.
  renewSubscription(_businessId: string, _subscriptionId: string, _updatePricesToCurrent = false): Observable<Subscription | null> {
    return of(null);
  }

  // ---------------------------------------------------------------------------
  // UTILITY
  // ---------------------------------------------------------------------------

  formatPrice(cents: number, currency = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(cents / 100);
  }

  // ---------------------------------------------------------------------------
  // DTO MAPPERS (backend response → frontend model)
  // ---------------------------------------------------------------------------

  private mapFeature = (r: FeatureResponse): Feature => ({
    id: r.id,
    code: r.code,
    name: r.name,
    description: r.description,
    type: r.type as FeatureType,
    category: r.category as FeatureCategory,
    unitName: r.unitName,
    sortOrder: r.sortOrder,
    isActive: r.isActive,
    createdAt: r.createdAt
  });

  private mapSubscriptionProduct = (r: SubscriptionProductResponse): SubscriptionProduct => ({
    id: r.id,
    featureId: r.featureId,
    featureName: r.featureName,
    featureCode: r.featureCode,
    featureCategory: r.featureCategory as FeatureCategory,
    billingInterval: r.billingInterval as BillingInterval,
    price: r.price,
    currency: r.currency,
    taxCode: r.taxCode,
    isActive: r.isActive,
    createdAt: r.createdAt
  });

  private mapMeteredProduct = (r: MeteredProductResponse): MeteredProduct => ({
    id: r.id,
    featureId: r.featureId,
    featureName: r.featureName,
    featureCode: r.featureCode,
    name: r.name,
    metricKey: r.metricKey,
    unitPrice: r.unitPrice,
    unitQuantity: r.unitQuantity,
    currency: r.currency,
    description: r.description,
    taxCode: r.taxCode,
    isActive: r.isActive,
    createdAt: r.createdAt
  });

  private mapPlan = (r: PlanResponse): Plan => ({
    id: r.id,
    name: r.name,
    description: r.description,
    billingInterval: (r.billingInterval ?? 'MONTHLY') as BillingInterval,
    currency: r.currency ?? 'USD',
    trialDays: r.trialDays,
    isActive: r.isActive,
    sortOrder: r.sortOrder,
    items: (r.items ?? []).map(item => this.mapPlanItem(item, r.id, r.currency)),
    totalPrice: r.totalPrice ?? (r.items ?? []).reduce((sum, i) => sum + (i.unitPrice ?? i.price), 0),
    listTotalPrice: r.listTotalPrice ?? (r.items ?? []).reduce((sum, i) => sum + i.price, 0),
    createdAt: r.createdAt
  });

  private mapPlanItem = (r: PlanItemResponse, planId: string, planCurrency?: string): PlanItem => ({
    id: r.id,
    planId,
    subscriptionProductId: r.subscriptionProductId,
    featureName: r.featureName,
    featureCode: r.featureCode,
    billingInterval: r.billingInterval as BillingInterval,
    price: r.price,
    unitPrice: r.unitPrice ?? r.price,
    currency: planCurrency
  });

  private mapSubscription = (r: SubscriptionResponse): Subscription => ({
    id: r.id,
    businessId: r.businessId,
    planId: r.planId,
    billingInterval: r.billingInterval as BillingInterval,
    status: r.status as SubscriptionStatus,
    currency: r.currency,
    startDate: r.startDate,
    trialEndDate: r.trialEndDate,
    canceledAt: r.canceledAt,
    lastBilledAt: r.lastBilledAt,
    createdAt: r.createdAt,
    // Frontend-only fields (`endDate`, `termMonths`, `autoRenew`) — not on
    // backend response. Defaults are safe; the UI doesn't currently rely
    // on them being authoritative.
    autoRenew: true,
    items: (r.items ?? []).map(it => this.mapSubscriptionItem(it, r.id))
  });

  private mapSubscriptionItem = (r: SubscriptionItemResponse, subscriptionId: string): SubscriptionItem => ({
    id: r.id,
    subscriptionId,
    subscriptionProductId: r.subscriptionProductId,
    featureCode: r.featureCode,
    featureName: r.featureName,
    unitPrice: r.unitPrice,
    quantity: r.quantity,
    locationId: r.locationId,
    createdAt: r.createdAt
  });
}
